import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnalytics } from '@bonusly/analytics';
import { BookmarkOutline, CheckedBookmarkSolid, IconButton, rem, Tooltip, useToast } from '@bonusly/design-system';

import { useBookmark } from '../../context';
import { BookmarkEvent } from '../../types/event';
import { useCreateBookmark } from '../bookmark/hooks/use-create-bookmark';
import { useDeleteBookmark } from '../bookmark/hooks/use-delete-bookmark';

export const BookmarkIconButton: FC = () => {
	const { t } = useTranslation('bookmarks');
	const { track } = useAnalytics();
	const { bookmark, referenceId, referenceType } = useBookmark();
	const toast = useToast();

	const isBookmarked = bookmark !== undefined;

	const icon = isBookmarked ? <CheckedBookmarkSolid size="sm" boxSize={rem(40)} /> : <BookmarkOutline size="sm" />;

	const options = {
		onSuccess: (response: any) => {
			let title = t('success_create');
			let event = BookmarkEvent.NewBookmark;

			if (response.config.method === 'delete') {
				event = BookmarkEvent.UnsaveBookmark;
				title = t('bookmark_modal.success_delete');
			}

			track(event);

			toast({
				position: 'top-right',
				title,
				isClosable: true,
				status: 'success',
			});
		},
	};

	const { mutate: createBookmark } = useCreateBookmark({ options });

	const { mutate: deleteBookmark } = useDeleteBookmark({
		options,
		id: bookmark?.id,
	});

	const handleOnClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
		if (isBookmarked) {
			deleteBookmark();
		} else {
			createBookmark({
				notes: '',
				reference_id: referenceId,
				reference_type: referenceType,
				label_ids: [],
			});
		}
		event.stopPropagation();
	};

	return (
		<Tooltip label={isBookmarked ? t('unbookmark_tooltip') : t('bookmark_tooltip')} placement="top">
			<IconButton
				_hover={{
					backgroundColor: 'primary.bgAlphaHover',
					color: 'primary.bg',
				}}
				data-testid="bookmark-icon-button"
				aria-label={t('bookmark')}
				backgroundColor={isBookmarked ? 'primary.bgAlpha' : 'transparent'}
				borderRadius="full"
				color={isBookmarked ? 'primary.bg' : 'text.default'}
				onClick={handleOnClick}
				boxSize={rem(40)}
				icon={icon}
			/>
		</Tooltip>
	);
};
